'use client';

import { useMemo } from 'react';

import { Spacer, SpacerSize, Title, TitleLevels, TitleMarkup } from '@bytel/trilogy-react';

import { ShortListContext } from '@app-types/eligibility';

import { LetterVariant } from '@components/letter-variant';

import { isFirstLetterVowel } from '@helpers/wording';

import { ERROR_CONTEXT, TECHNOLOGY } from '@constants/provider';

import { EligibilityResults, JourneyConfigType, isEligibleToFocusOffer } from '@eligibility/helpers';

interface EligibilityTitleProps {
    config: JourneyConfigType;
    shortList?: ShortListContext;
    results?: EligibilityResults;
}

export function EligibilityTitle({ config, shortList, results }: Readonly<EligibilityTitleProps>) {
    const { title, shouldShowSubtitle } = useMemo(() => {
        if (results) {
            if (results.error === ERROR_CONTEXT.ERROR) {
                return { title: config.wordings.mainTitle, shouldShowSubtitle: true };
            }

            if (isEligibleToFocusOffer(shortList, results)) {
                return {
                    title: (
                        <>
                            Bonne n<LetterVariant letter='o' />
                            uvelle, vous pouvez profiter de {isFirstLetterVowel(shortList?.name ?? '') ? "l'" : 'la '}
                            {shortList?.name}&nbsp;!
                        </>
                    ),
                    shouldShowSubtitle: false,
                };
            }

            const techno = results.technology;

            if (Object.values(results.offers).length === 0) {
                return { title: <FibreUnavailable />, shouldShowSubtitle: false };
            }

            switch (techno) {
                case TECHNOLOGY.FTTH:
                    return {
                        title: (
                            <>
                                Bonne nouvelle, la fi
                                <LetterVariant letter='b' />
                                re est disponible&nbsp;!
                            </>
                        ),
                        shouldShowSubtitle: false,
                    };
                case TECHNOLOGY.BOX_4G:
                case TECHNOLOGY.BOX_5G:
                default:
                    return { title: <FibreUnavailable />, shouldShowSubtitle: false };
            }
        }

        if (shortList?.name) {
            return {
                title: (
                    <>
                        Vous avez ch
                        <LetterVariant letter='o' />
                        isi {isFirstLetterVowel(shortList.name) ? "l'" : 'la '}
                        {shortList.name}. <br />
                        On teste votre éligibilité&nbsp;?
                    </>
                ),
                shouldShowSubtitle: false,
            };
        }

        return { title: config.wordings.mainTitle, shouldShowSubtitle: true };
    }, [results, shortList]);

    return (
        <>
            <Title
                markup={TitleMarkup.H1}
                level={TitleLevels.TWO}
                className='has-text-centered-tablet whitespace-pre-line'
                data-cy='page-title'
            >
                {title}
            </Title>
            {shouldShowSubtitle && config.wordings.subtitle && (
                <Title level={TitleLevels.FIVE} className='has-text-centered-tablet whitespace-pre-line'>
                    {config.wordings.subtitle}
                </Title>
            )}
            <Spacer size={SpacerSize.TWO} />
        </>
    );
}

const FibreUnavailable = () => {
    return (
        <>
            La fi
            <LetterVariant letter='b' />
            re n&apos;est pas encore disponible ici
        </>
    );
};
